import { MERGE_FIELDS, STYLE_SOURCES, type HtmlTemplateDocument } from './template'

/**
 * The HTML-first template contract: what an author — or Claude, handed the prompt below — may
 * write into a template, and what the renderer expands. One definition, read by three things that
 * must never disagree: the prompt (`htmlTemplatePrompt`), the renderer
 * (`@headline/report-render`, `html-template.ts`) and the editor's warnings. Change a name here and
 * the renderer's `satisfies` check fails to compile, which is the point.
 *
 * Ported from Phoebe's sandbox prototype (`webapp2.0-prototype`, Templates v2): the loop is copy
 * the prompt → paste it into Claude with the client's screenshots → paste the HTML it returns →
 * preview. The markers are hers; the token vocabulary is hers merged with the custom row DSL the
 * block templates already speak, so a row copied from one works in the other — and so a block
 * template translated into this shape (`blocksToHtmlTemplate` in report-render) renders the same
 * email it did.
 */

/**
 * Every source a clips block may claim. The first six are what the pipeline produces today; the
 * five other networks are accepted now so a client's format can be written once and light up as
 * each collector lands — a block for a source with no coverage simply renders empty.
 */
export const CLIP_TYPES = [
  'news',
  'broadcast',
  'podcast',
  'x',
  'linkedin',
  'newsletter',
  'bluesky',
  'instagram',
  'facebook',
  'reddit',
  'truth',
] as const
export type ClipType = (typeof CLIP_TYPES)[number]

export const CLIP_TYPE_LABEL: Record<ClipType, string> = {
  news: 'News',
  broadcast: 'Broadcast',
  podcast: 'Podcast',
  x: 'X',
  linkedin: 'LinkedIn',
  newsletter: 'Newsletter',
  bluesky: 'Bluesky',
  instagram: 'Instagram',
  facebook: 'Facebook',
  reddit: 'Reddit',
  truth: 'Truth Social',
}

/** The sources articles can actually carry today — the rest of CLIP_TYPES wait on their collectors. */
export const LIVE_CLIP_TYPES: readonly ClipType[] = STYLE_SOURCES.map((source) => source.key)

export function isClipType(value: string): value is ClipType {
  return (CLIP_TYPES as readonly string[]).includes(value)
}

/** `data-cb-slot` names: where a report's own words land. */
export const SLOT_NAMES = ['greeting', 'intro', 'signoff'] as const
export type SlotName = (typeof SLOT_NAMES)[number]

/**
 * `data-cb` marker names: elements the renderer drops when they have nothing to say. The first
 * four are judged per clip, `toc` and `insights` per email, `when-mixed` per header — it marks a
 * group heading that should only show when the header's clips fall into more than one clips block.
 */
export const MARKER_NAMES = [
  'syndicated',
  'opinion',
  'paywall',
  'translated',
  'toc',
  'insights',
  'when-mixed',
] as const
export type MarkerName = (typeof MARKER_NAMES)[number]

/** `data-cb-repeat` region names. */
export const REPEAT_NAMES = [
  'headers',
  'clips',
  'snippets',
  'toc-items',
  'toc-clips',
  'insight-items',
] as const
export type RepeatName = (typeof REPEAT_NAMES)[number]

/**
 * The attributes the contract reads beyond the markers and regions. Named here so the sanitiser's
 * allowlist and the renderer's stripping pass can never disagree about what belongs to the
 * contract (`data-cb`, `data-cb-repeat` and `data-cb-slot` are the three above).
 */
export const CONTRACT_ATTRIBUTES = [
  'data-cb',
  'data-cb-repeat',
  'data-cb-slot',
  /** On a clips block: the comma-separated sources it renders. Absent means the catch-all. */
  'data-cb-types',
  /** On a wrapper: removed when no clip rendered inside it. */
  'data-cb-group',
  /**
   * On a clips block: how many snippets at most, for every snippet token inside it; on a snippets
   * repeat: the same, for that repeat. Absent means all of them.
   */
  'data-cb-limit',
  /**
   * On a clips block (or any ancestor; the nearest wins): the background behind a matched term,
   * or `none` to strip the highlights and keep the words. Absent leaves the producer's `<mark>`.
   */
  'data-cb-highlight',
  /** Beside `data-cb-highlight`: the background behind a principal's name. */
  'data-cb-highlight-principal',
  /** On a headers or toc-items repeat: keep headers that produced nothing today. */
  'data-cb-show-empty',
  /**
   * On a clips block: what `{{source_label}}` and `{{platform}}` say for its clips — "in" for a
   * LinkedIn badge, "Twitter" for a client who still says so. Absent means the source's own name.
   */
  'data-cb-label',
  /**
   * On the headers element: the order a header's clips take before an associate arranges them —
   * `ranked` (the pull's order), `newest`, `outlet`, `alpha` or `priority` (the header's outlet
   * ranking). Absent means ranked. A header with a format of its own keeps its own sort.
   */
  'data-cb-sort',
  /**
   * On a clips block: at most this many outlets on the syndicated line (`{{syndicated}}` and
   * `{{syndicated_links}}`). Absent means every outlet that ran the article.
   */
  'data-cb-syndication-limit',
  /** Beside it: `alpha` lists those outlets A–Z. Absent keeps the producer's order, newest first. */
  'data-cb-syndication-sort',
] as const

/**
 * Per-clip tokens and what each fills in as, grouped the way the prompt explains them. Every
 * source resolves EVERY token (a post's `headline` is its opening line, its `outlet` the account
 * name), so a catch-all block written in article shape still renders a tweet sensibly.
 */
export const CLIP_TOKENS = {
  common: [
    ['url', 'the link to the coverage'],
    ['date', 'the date, short (Oct 8, 2026)'],
    ['date_long', 'the date with weekday and time (Thursday, October 08, 06:35:46 AM)'],
    ['source_label', 'the source, as the report labels it ("News", "X", "Broadcast")'],
    ['snippet', 'the matched excerpts run together — or ONE excerpt, inside a snippets repeat'],
    ['snippet_items', 'the matched excerpts as <li> items, for a list'],
    ['summary', 'the one-line AI summary, when the report has one'],
    ['syndicated', 'the outlets that also ran the story, comma-separated'],
    ['syndicated_links', 'the same outlets, each linked to its copy'],
    ['syndicate_count', 'how many outlets syndicated it'],
    ['full_text_url', 'the licensed full-text link, when there is one'],
    ['translated_from', 'the language an associate translated the article from, when they did'],
    ['reach', 'the outlet’s reach, abbreviated, when known'],
    ['sentiment', 'the sentiment label, when the report has one'],
    [
      'is_opinion',
      'set when the piece is flagged as opinion — use it as a condition: {{#if is_opinion}}[Opinion] - {{/if}}',
    ],
    ['is_paywalled', 'set when the article is paywalled — a condition, like is_opinion'],
  ],
  news: [
    ['headline', 'the article’s headline'],
    ['outlet', 'the outlet'],
    ['author', 'the byline'],
  ],
  broadcast: [
    ['show', 'the programme (same as outlet)'],
    ['station', 'the station (same as outlet)'],
    ['transcript', 'the transcript excerpt (same as snippet)'],
  ],
  podcast: [
    ['show', 'the podcast (same as outlet)'],
    ['episode', 'the episode title (same as headline)'],
    ['transcript', 'the transcript excerpt (same as snippet)'],
  ],
  social: [
    ['account_name', 'the account’s display name'],
    ['handle', 'the @handle, when known'],
    ['post_text', 'the text of the post'],
    ['platform', 'the network ("X", "LinkedIn", …)'],
    ['engagement', 'likes/replies/reposts, when known'],
    ['followers', 'the account’s followers, abbreviated, when known'],
  ],
} as const satisfies Record<string, readonly (readonly [string, string])[]>

/** Every per-clip token name, flat — what the renderer must resolve and the editor recognises. */
export const CLIP_TOKEN_NAMES = [
  ...new Set(Object.values(CLIP_TOKENS).flatMap((group) => group.map(([token]) => token))),
] as const
export type ClipToken = (typeof CLIP_TOKEN_NAMES)[number]

/** Tokens that resolve per header, inside the `headers` region. */
export const HEADER_TOKENS = [
  ['header_name', 'the header’s name'],
  ['header_subtitle', 'the header’s subtitle, when it has one'],
] as const
export type HeaderToken = (typeof HEADER_TOKENS)[number][0]

/** Inside `insight-items`: the insight, and its citations as links when the report has them. */
export const INSIGHT_TOKENS = [
  ['insight', 'the insight'],
  ['citations', 'its sources, linked, when the report cites them'],
] as const
export const INSIGHT_TOKEN = 'insight'

/**
 * The account's brand colour, as `#rrggbb`, anywhere in the email — `style="color:{{accent}}"`.
 * What lets one HTML template serve every account in its own colour, the way a block template's
 * section rules and links always did.
 */
export const ACCENT_TOKEN = 'accent'

/** Whole-email tokens: the merge fields plus the brand colour. */
export const EMAIL_TOKEN_NAMES: readonly string[] = [
  ...MERGE_FIELDS.map((field) => field.token),
  ACCENT_TOKEN,
]

/** Every token name the contract resolves somewhere — what the editor checks a `{{name}}` against. */
export const KNOWN_TOKEN_NAMES: ReadonlySet<string> = new Set<string>([
  ...EMAIL_TOKEN_NAMES,
  ...HEADER_TOKENS.map(([token]) => token),
  ...CLIP_TOKEN_NAMES,
  ...INSIGHT_TOKENS.map(([token]) => token),
])

const SOCIAL_TYPES: readonly ClipType[] = [
  'x',
  'linkedin',
  'bluesky',
  'instagram',
  'facebook',
  'reddit',
  'truth',
]

/**
 * The copyable prompt. Deliberately written for a chatbot, not for this app: self-contained, so
 * pasting it into a fresh Claude conversation with nothing else still produces usable HTML. Built
 * from the tables above so it cannot name a token the renderer does not resolve.
 */
export function htmlTemplatePrompt(): string {
  const row = (entries: readonly (readonly [string, string])[]) =>
    entries.map(([token, meaning]) => `| \`{{${token}}}\` | ${meaning} |`).join('\n')
  const emailTokens = [
    ...MERGE_FIELDS.map((field) => [field.token, field.label.toLowerCase()] as const),
    [
      ACCENT_TOKEN,
      'the client’s brand colour as #rrggbb — use it in style="" where their colour goes',
    ] as const,
  ]
  return `You are helping build an HTML email template for Clipbook, a media-monitoring company. Clipbook analysts send clients a regular "report" email: a curated digest of press coverage mentioning that client, grouped under headers, with a short greeting at the top and a sign-off.

I will give you either a written description of the format a client wants, or screenshots of a format they already receive. Your job is to return the HTML for that format.

## How Clipbook reports are structured

A report email reads top to bottom:

1. A greeting and a one- or two-line intro ("Good morning," / "Sharing press clips for today.").
2. One or more **headers** — named sections such as "Top coverage" or "Social media". Each header holds the coverage that belongs to it.
3. Inside each header, a list of **clips**. A clip is one piece of coverage.
4. A sign-off ("Best, Clipbook Team"). Some clients put the sign-off ABOVE the headers; match what the client's format shows.

## Clipbook monitors several kinds of source, and they are not the same shape

A news article has a headline, an outlet and a byline. A broadcast segment or podcast episode has a show and a transcript excerpt. A social post has an account, a handle and the text of the post — no headline.

| Source | Shape |
|---|---|
| News, Newsletter | headline + outlet + author + snippet |
| Broadcast, Podcast | show + transcript excerpt |
| ${SOCIAL_TYPES.map((type) => CLIP_TYPE_LABEL[type]).join(', ')} | account name + handle + post text |

So a template needs **a clip format per source** — one block per source, or one block shared by the sources that look alike. Every source listed below must be covered by a typed block or a catch-all block.

## How to give it to me — read this first

**Put the HTML in one fenced code block in your reply, as text I can copy.** Do not create an artifact, a file, a download link or a rendered preview; I am going to paste the raw HTML into Clipbook's template editor. One \`\`\`html block containing the whole fragment is right. Keep any explanation to a sentence or two after it.

## What to return

Return **one self-contained HTML fragment** — the body of the email, nothing else:

- No \`<!DOCTYPE>\`, \`<html>\`, \`<head>\` or \`<body>\` tags. Start with the outermost \`<div>\`.
- **Inline styles only** (\`style="..."\`). No \`<style>\` blocks, no classes for styling — email clients strip them.
- Email-safe: web-safe font stacks with fallbacks, no flexbox or grid for anything load-bearing, no JavaScript, no external images unless I gave you a URL.
- Match the client's format as closely as the screenshots or description show: fonts, colours, spacing, and the order things appear in. If they want it narrow and centred, constrain and centre it; if their current email runs full width from the left edge, do that.

## Placeholders you must use

The same HTML is sent every time with different coverage in it, so write placeholders, not real content. Use these exact tokens — Clipbook fills them in at send time.

Whole-email values:

| Token | Fills in as |
|---|---|
${row(emailTokens)}

## The greeting, intro and sign-off

Wrap each of these in an element tagged with \`data-cb-slot\`, with the client's usual words inside. Clipbook replaces the inside of a slot when a particular report has its own words, and removes the sign-off slot when a report hides it:

\`\`\`
<div data-cb-slot="greeting"><p>Good morning,</p></div>
<div data-cb-slot="intro"><p>Sharing press clips for today.</p></div>
<div data-cb-slot="signoff"><p>Best,<br>{{sender_name}}</p></div>
\`\`\`

## Repeating regions

Mark the element that should repeat, and write the inside of it **once**. The marked element itself is repeated — so mark the \`<li>\`, not the \`<ul>\`, when each item is a clip.

**Headers:** \`<div data-cb-repeat="headers"> ... </div>\`. Inside it:

| Token | Fills in as |
|---|---|
${row(HEADER_TOKENS)}

**Clips**, nested inside the headers region: \`<div data-cb-repeat="clips"> ... </div>\`. Add \`data-cb-types\` to say which sources that block renders — a comma-separated list from: ${CLIP_TYPES.map((type) => `\`${type}\``).join(', ')}. Write as many clips blocks as the format needs:

\`\`\`
<div data-cb-repeat="clips" data-cb-types="news,newsletter">…article format…</div>
<div data-cb-repeat="clips" data-cb-types="broadcast,podcast">…segment format…</div>
<div data-cb-repeat="clips" data-cb-types="${SOCIAL_TYPES.join(',')}">…post format…</div>
\`\`\`

A clips block with **no** \`data-cb-types\` is the catch-all: any source no other block claims renders there. Always include either a catch-all or blocks covering every source, or coverage the client paid for can go missing.

**Tokens inside a clip.** Common to every source:

| Token | Fills in as |
|---|---|
${row(CLIP_TOKENS.common)}

News and newsletters:

| Token | Fills in as |
|---|---|
${row(CLIP_TOKENS.news)}

Broadcast:

| Token | Fills in as |
|---|---|
${row(CLIP_TOKENS.broadcast)}

Podcast:

| Token | Fills in as |
|---|---|
${row(CLIP_TOKENS.podcast)}

Social (${SOCIAL_TYPES.map((type) => CLIP_TYPE_LABEL[type]).join(', ')}):

| Token | Fills in as |
|---|---|
${row(CLIP_TOKENS.social)}

\`{{headline}}\` and \`{{outlet}}\` still resolve on non-article sources (a post's text, the account name), so a catch-all block written in article shape degrades gracefully.

**Snippets as a list.** To style each matched excerpt as its own item, mark one item \`data-cb-repeat="snippets"\` and put \`{{snippet}}\` inside it. \`data-cb-limit="3"\` caps how many show — on the item, or on the clips block for every snippet token inside it:

\`\`\`
<ul><li data-cb-repeat="snippets" data-cb-limit="3" style="…">{{snippet}}</li></ul>
\`\`\`

**Naming a source.** \`{{source_label}}\` and \`{{platform}}\` say the source's own name ("X", "LinkedIn"); put \`data-cb-label="in"\` on a clips block to have them say something else for its clips.

**Highlighted terms.** Matched terms arrive wrapped in \`<mark>\`. Put \`data-cb-highlight="#cce0f5"\` on a clips block to paint them that colour instead (Clipbook's classic light blue), \`data-cb-highlight-principal="#fbf719"\` beside it for the client's own name, or \`data-cb-highlight="none"\` to show the words plain.

**Syndicated outlets.** \`{{syndicated}}\` lists the other outlets that ran an article. \`data-cb-syndication-limit="5"\` on the clips block caps the list; \`data-cb-syndication-sort="alpha"\` orders it A–Z instead of newest first.

**Conditionals.** A part that should appear only when a value exists goes inside \`{{#if token}} … {{/if}}\` — for example \`{{#if author}}By {{author}}{{/if}}\` or \`{{#if full_text_url}}<a href="{{full_text_url}}">Read More</a>{{/if}}\`. One token per condition, no nesting, no else. Use one around every optional part — the byline, the date, the summary, the snippets, the Read More link — because a report can switch any of them off for a header, and a switched-off value must leave no stray label behind.

**Group wrappers.** If a source group has its own heading ("Social media", "Broadcast & podcast"), wrap the heading and its clips block together in an element marked \`data-cb-group\`. Clipbook removes the whole wrapper when that group has no coverage in a send, so the client never sees a heading with nothing under it:

\`\`\`
<div data-cb-group>
  <h3>Social media</h3>
  <div data-cb-repeat="clips" data-cb-types="x,linkedin">…</div>
</div>
\`\`\`

A heading marked \`data-cb="when-mixed"\` shows only when its header's clips fall into more than one clips block — for a client who labels the groups only when there is something to tell apart. A block that takes every social network counts as one group.

## Elements Clipbook shows only when they apply

Tag these with \`data-cb\` and Clipbook drops them on clips where there is nothing to show:

| Attribute | Put it on |
|---|---|
| \`data-cb="syndicated"\` | the line listing outlets that syndicated the article |
| \`data-cb="opinion"\` | the badge that flags an opinion piece |
| \`data-cb="paywall"\` | the badge that flags a paywalled article |
| \`data-cb="translated"\` | the line saying the article was translated |
| \`data-cb="toc"\` | the whole table-of-contents block — mark its item \`data-cb-repeat="toc-items"\` and write it once with \`{{header_name}}\`; an item inside it marked \`data-cb-repeat="toc-clips"\` lists that header's \`{{headline}}\`s |
| \`data-cb="insights"\` | the whole AI-insights block — mark its item \`data-cb-repeat="insight-items"\` and write it once with \`{{${INSIGHT_TOKEN}}}\` (and \`{{citations}}\` for its sources) |

Headers that produced no coverage are left out of the email. If the client wants to see every header every day, add \`data-cb-show-empty\` to the headers element.

**Order of clips.** Clips arrive in the order Clipbook's analyst arranged them. To set the order a format starts from, put \`data-cb-sort\` on the headers element: \`newest\`, \`alpha\` (by headline), \`outlet\`, or \`priority\` (the header's outlet ranking). Leave it off for Clipbook's relevance order.

## Before you finish

Check that: every repeating region is marked and written out once; every source is covered by a typed block or a catch-all; every placeholder uses the exact spelling above; all styling is inline; and the fragment opens and closes cleanly.

And check the delivery: the HTML is in a **fenced code block in your reply as copyable text**, not an artifact, not a file, not a preview.

Here is what the client wants:`
}

/**
 * An HTML document around the given body. The house format — the standard layout written in this
 * contract — lives in `@headline/report-render` (`houseHtmlTemplateDocument`), because it is the
 * translation of the standard block document and translating takes the renderer.
 */
export function htmlTemplateDocument(html: string): HtmlTemplateDocument {
  return { version: 2, kind: 'html', target: 'email', html }
}

/**
 * Strips the ```html fence a chatbot wraps its answer in, so a paste of the whole reply lands as
 * markup. Anything outside the first fence is dropped — that is the sentence of explanation the
 * prompt allows after the code.
 */
export function stripCodeFence(text: string): string {
  return fencedBody(text) ?? text
}

/**
 * Where the body of a fence opened at `after` (just past its backticks) begins: past an optional
 * `html`, the run of whitespace after it, up to and including that run's last newline. -1 when the
 * run holds no newline — the opening line must end there.
 */
function fenceBodyStart(text: string, after: number): number {
  const lang = /^html/i.test(text.slice(after, after + 4)) ? after + 4 : after
  let bodyStart = -1
  for (let i = lang; i < text.length && /\s/.test(text[i]!); i += 1) {
    if (text[i] === '\n') bodyStart = i + 1
  }
  return bodyStart
}

/**
 * The first fenced block's body — what `/```(?:html)?\s*\n([\s\S]*?)```/i` captured, as a scan
 * rather than a backtracking regex (CLI-876). Undefined when there is no complete fence.
 */
function fencedBody(text: string): string | undefined {
  let from = 0
  for (;;) {
    const open = text.indexOf('```', from)
    if (open === -1) return undefined
    const bodyStart = fenceBodyStart(text, open + 3)
    if (bodyStart !== -1) {
      const close = text.indexOf('```', bodyStart)
      // No closing fence after this body means none after any later opening either.
      return close === -1 ? undefined : text.slice(bodyStart, close)
    }
    from = open + 1
  }
}
